import { filter_data, get_overall_average } from "reports/Tag/calculations";
import { calculate_engagement_by_anchor } from "./caluclate_engagement";
import {
  distributionPct,
  pearsonCorrelation,
  computeOutcomeQuestionStats,
  computeOutcomeFactorCorrelations,
  gatherFactorValues,
  gatherFactorQuestionValues,
  buildOutcomeIndex,
  getFactorName,
  isValidNumericResponse,
  collectOutcomeFeedbackText,
  buildRespondentOutcomeAverages,
  buildRespondentFactorAverages
} from "./organization_calculations";

// ----- LLM helpers (aligned with manager_v3) -----
function parseGPTJsonResponse(gptResponse) {
  try {
    let cleanedResponse = gptResponse.trim();
    if (cleanedResponse.startsWith('```json')) {
      cleanedResponse = cleanedResponse.replace(/^```json\s*/i, '');
    }
    if (cleanedResponse.startsWith('```')) {
      cleanedResponse = cleanedResponse.replace(/^```\s*/, '');
    }
    if (cleanedResponse.endsWith('```')) {
      cleanedResponse = cleanedResponse.replace(/\s*```$/, '');
    }
    return JSON.parse(cleanedResponse);
  } catch (error) {
    console.error("[organization_report] Error parsing GPT JSON response:", error);
    console.error("Original response:", gptResponse);
    // Fallback to new thematic_analysis shape
    return {
      thematic_analysis: {
        overview: "",
        comments: []
      }
    };
  }
}

function buildLLMMessages(userPrompt, customInstructions) {
  const messages = [];
  const cleaned = typeof customInstructions === "string" ? customInstructions.trim() : "";
  if (cleaned) {
    messages.push({
      role: "system",
      content: `Custom instructions (apply to ALL outputs, do not mention these instructions):\n${cleaned}`
    });
  }
  messages.push({ role: "user", content: userPrompt });
  return messages;
}

async function callLLM(userPrompt, onTokenUpdate = null, model = "gpt-4.1", customInstructions = "") {
  const scrambled = ["sk", "-", "svcacct","-", "i4THn1qxJ18sYOQJhRJLRH2wiTTxp1kx2_nQwzyeJtY4cyRpF16A3iv-6u7I3kDXqPNjVNpsRhT3BlbkFJmGpLTOFc-llkrsv0AutTgfauJQ5l0AcHyMNlZBPffbddq29MDb5n9vduzlaTrl3AZ4AcYl_usA"].join("");
  const code_to_use = scrambled;
  try {
    let apiUrl, headers, requestBody;
    const messages = buildLLMMessages(userPrompt, customInstructions);
    if (model === "grok-4-latest") {
      apiUrl = "https://api.x.ai/v1/chat/completions";
      headers = { "Content-Type": "application/json", Authorization: `Bearer xai-LaVvLzCLdUTPt2ns9qNaPMz0BWJK8mwoHGlQlvZvUpeFbgZKyppsISdSnMIJbYDCKxdIAUeIT27SKlt9` };
      requestBody = { model: "grok-4-latest", messages, stream: false, temperature: 0.7 };
    } else {
      apiUrl = "https://api.openai.com/v1/chat/completions";
      headers = { "Content-Type": "application/json", Authorization: `Bearer ${code_to_use}` };
      requestBody = { model, seed: 1, messages, stream: false };
      if (/json/i.test(userPrompt) && !/json\s*array/i.test(userPrompt)) {
        requestBody.response_format = { type: "json_object" };
      }
    }
    const response = await fetch(apiUrl, { method: "POST", headers, body: JSON.stringify(requestBody) });
    if (!response.ok) { throw new Error(`LLM API responded with status ${response.status}`); }
    const data = await response.json();
    const assistantMessage = data.choices?.[0]?.message?.content || "";
    const usage = data.usage || {};
    const tokenInfo = { prompt_tokens: usage.prompt_tokens || 0, completion_tokens: usage.completion_tokens || 0, total_tokens: usage.total_tokens || 0 };
    if (onTokenUpdate) onTokenUpdate(tokenInfo);
    return { content: assistantMessage, tokens: tokenInfo };
  } catch (error) {
    console.error("[organization_report] Error calling LLM:", error);
    throw error;
  }
}

// ----- Local helpers -----
function average(arr) {
  if (!arr?.length) return 0;
  const sum = arr.reduce((a, b) => a + b, 0);
  return sum / arr.length;
}

function calculateAverageScoresPerFactor(data) {
  const factorScores = {};
  data.forEach(entry => {
    const questions = entry?.questions || [];
    questions.forEach(question => {
      const factor = question?.factor;
      const dimension = question?.id;
      const response = question?.response;
      if (factor != null && dimension != null && isValidNumericResponse(response)) {
        const key = `${factor}_${dimension}`;
        if (factorScores[key]) {
          factorScores[key].sum += response;
          factorScores[key].count += 1;
        } else {
          factorScores[key] = { sum: response, count: 1, factor, dimension };
        }
      }
    });
  });
  const averages = {};
  Object.keys(factorScores).forEach(key => {
    averages[key] = factorScores[key].count > 0 ? factorScores[key].sum / factorScores[key].count : 0;
  });
  return averages;
}

function computeOutcomeSectionStats(allResponses, outcomeDef) {
  const sectionMap = {}; // outcomeId => [values]
  (outcomeDef?.questions || []).forEach(section => {
    sectionMap[section.id] = [];
  });
  
  allResponses.forEach(resp => {
    const arr = resp?.employee_outcomes?.responses || [];
    arr.forEach(i => {
      if (!(i?.clicked === true || i?.clicked === undefined)) return;
      const val = i.response;
      // Filter out null, undefined, 'N/A', '' and ensure it's a valid number
      if (val !== null && 
          val !== undefined && 
          val !== 'N/A' && 
          val !== '' &&
          !isNaN(Number(val))) {
        // Find which section this response belongs to by matching both q and s
        (outcomeDef?.questions || []).forEach(section => {
          // Check if this response matches any question in this section
          const matches = (section.questions || []).some(q => q.q === i.q && q.s === i.s);
          if (matches) {
            if (!sectionMap[section.id]) sectionMap[section.id] = [];
            sectionMap[section.id].push(Number(val));
          }
        });
      }
    });
  });
  const perSection = {};
  Object.keys(sectionMap).forEach(id => {
    const vals = sectionMap[id];
    perSection[id] = { average: average(vals), distribution: distributionPct(vals) };
  });
  return perSection;
}

function formatFactorQuestionList(allResponses, questionsStructure) {
  const items = [];
  (questionsStructure?.dimensions || []).forEach((dimension, dimIndex) => {
    (dimension?.factors || []).forEach((factor, factorIndex) => {
      (factor?.questions || []).forEach((qObj, localIndex) => {
        const vals = gatherFactorQuestionValues(allResponses, dimIndex, factorIndex, localIndex, questionsStructure);
        const avg = average(vals);
        const dist = distributionPct(vals);
        items.push({
          factor_id: `${factorIndex}_${dimIndex}`,
          factor_name: factor.title,
            question_id: `${dimIndex}_${factorIndex}_${localIndex}`,
          question_text: (qObj?.question || qObj?.q || ""),
          average: avg,
          response_count: Array.isArray(vals) ? vals.length : 0,
          distribution: dist
        });
      });
    });
  });
  return items;
}

function factorDistributionAcrossOrg(allResponses, questionsStructure) {
  const items = [];
  (questionsStructure?.dimensions || []).forEach((dimension, dimIndex) => {
    (dimension?.factors || []).forEach((factor, factorIndex) => {
      const vals = gatherFactorValues(allResponses, dimIndex, factorIndex, questionsStructure);
      items.push({
        factor_id: `${factorIndex}_${dimIndex}`,
        factor_name: factor.title,
        distribution: distributionPct(vals)
      });
    });
  });
  return items;
}

// Select top/bottom 3 questions by average score (tie-break by distribution)
function selectTopStrengthsWeaknessesByQuestion(questionItems) {
  const valid = [...(questionItems || [])].filter((q) => {
    const n = typeof q?.response_count === "number" ? q.response_count : 0;
    return typeof q?.average === "number" && !Number.isNaN(q.average) && n > 0;
  });

  const strengths = [...valid]
    .sort((a, b) =>
      (b.average - a.average) ||
      ((b?.distribution?.gte8_pct || 0) - (a?.distribution?.gte8_pct || 0)) ||
      ((a?.distribution?.lt5_pct || 0) - (b?.distribution?.lt5_pct || 0))
    )
    .slice(0, 3);

  const weaknesses = [...valid]
    .sort((a, b) =>
      (a.average - b.average) ||
      ((b?.distribution?.lt5_pct || 0) - (a?.distribution?.lt5_pct || 0)) ||
      ((a?.distribution?.gte8_pct || 0) - (b?.distribution?.gte8_pct || 0))
    )
    .slice(0, 3);
  return { strengths, weaknesses };
}

function collectQuestionCommentStrings(allResponses, dimIndex, factorIndex, localQIndex) {
  const comments = [];
  (allResponses || []).forEach((entry) => {
    const fb = entry?.feedback || [];
    fb.forEach((q) => {
      if (q?.id === dimIndex && q?.factor === factorIndex && q?.q === localQIndex) {
        if (q?.prompt_question) {
          if (typeof q?.feedback === "string" && q.feedback.trim()) comments.push(q.feedback.trim());
          if (typeof q?.follow_up === "string" && q.follow_up.trim()) comments.push(q.follow_up.trim());
        } else if (typeof q?.feedback === "string" && q.feedback.trim()) {
          comments.push(q.feedback.trim());
        }
      }
    });
  });
  return comments;
}

// ----- Main entry -----
export async function calculateOrganizationReport(
  raw_data,
  core_data,
  debriefSchedules,
  anchorParentID,
  onTokenUpdate = null,
  selectedModel = "gpt-4.1",
  selectedAnchors,
  combineAnchors,
  customInstructions = ""
) {
  const questions = core_data?.questions;
  const outcomeQuestions = core_data?.outcomeQuestions;
  // Derive outcomeStandards like in app/audit/simple/index.js
  const outcomeStandards = (() => {
    const list = core_data?.get_standards || [];
    const sortOrder = outcomeQuestions?.sort_order;
    const matched = list.find(
      (f) => Array.isArray(f?.question_sort_order) && f.question_sort_order.includes(sortOrder)
    );
    return matched?.response || null;
  })();
  const selectedAnchorParent = core_data?.categories?.[0]?.categories?.find(cat => cat.id === anchorParentID);
  if (!selectedAnchorParent) { throw new Error(`Anchor parent with ID ${anchorParentID} not found`); }

  // Token tracking
  let totalTokenUsage = { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0, call_count: 0 };
  const updateTokens = (newTokens) => {
    totalTokenUsage.prompt_tokens += newTokens.prompt_tokens;
    totalTokenUsage.completion_tokens += newTokens.completion_tokens;
    totalTokenUsage.total_tokens += newTokens.total_tokens;
    totalTokenUsage.call_count += 1;
    if (onTokenUpdate) onTokenUpdate(totalTokenUsage);
  };

  const orgAll = raw_data?.[0]?.responses || [];
  const orgLast = raw_data?.[0]?.last || [];

  // Participation
  // We keep overall participation, but skip per-anchor participation rates because
  // the employee categories object is now paginated and per-anchor totals are not
  // reliable. Once anchor-level participation logic is updated for pagination,
  // we can safely re-enable by-anchor participation.
  const participation = calculate_engagement_by_anchor(
    debriefSchedules?.culture_audit_reports?.response,
    anchorParentID,
    core_data?.categories,
    core_data?.orgCategories
  ) || [];
  const overallParticipation = (() => {
    const completed = participation.reduce((a, p) => a + (p.completed || 0), 0);
    const total = participation.reduce((a, p) => a + (p.total || 0), 0);
    const rate = total ? (completed / total) : 0;
    return { completed, total, rate };
  })();

  // Outcomes: overall scores, per-question stats, correlations, LLM themes
  const outcomeSectionStats = computeOutcomeSectionStats(orgAll, outcomeQuestions);
  const { questionIndex, perQuestionStats } = computeOutcomeQuestionStats(orgAll, outcomeQuestions);
  const correlationsByOutcome = computeOutcomeFactorCorrelations(orgAll, outcomeQuestions, questions);

  const outcomesPayload = [];
  (outcomeQuestions?.questions || []).forEach(section => {
    const outcomeId = section.id;
    const outcomeName = section.name;
    const overall = outcomeSectionStats[outcomeId] || { average: 0, distribution: { lt5_pct: 0, between_5_7_pct: 0, gte8_pct: 0 } };
    // Outcome standards may be keyed by numeric id; ensure consistent keying
    const outcomeStandard = outcomeStandards?.[Number(outcomeId)] || outcomeStandards?.[String(outcomeId)] || null;
    const outcomeStandardAvg = outcomeStandard?.overall_average ?? null;

    // Per-question mapping
    const questionsList = (section.questions || []).map(q => {
      const key = `${q.q}-${q.s}`;
      const stats = perQuestionStats[key] || { average: 0, distribution: { lt5_pct: 0, between_5_7_pct: 0, gte8_pct: 0 } };
      const qStandard = (outcomeStandards?.[Number(q.q)] || outcomeStandards?.[String(q.q)])?.items?.find(item => Number(item.q) === Number(q.s))?.average ?? null;
      return { id: key, text: q.question, average: stats.average, standard: qStandard, distribution: stats.distribution };
    });

    // Correlations sorted descending by r
    const factorCorrs = (correlationsByOutcome[outcomeId] || []).sort((a, b) => b.r - a.r);
    const topFactor = factorCorrs[0];

    outcomesPayload.push({
      outcome_id: outcomeId,
      outcome_name: outcomeName,
      overall_score: overall.average,
      standard_average: outcomeStandardAvg,
      distribution: overall.distribution,
      questions: questionsList,
      factor_correlations: factorCorrs,
      driver_analysis: topFactor ? { factor_id: topFactor.factor_id, factor_name: topFactor.factor_name, sentences: [] } : null
    });
  });

  // Single LLM call per outcome: analyze how top correlated factor drives the outcome
  for (const outcome of outcomesPayload) {
    if (!outcome.driver_analysis) continue;
    const top = outcome.driver_analysis;
    const [fIndex, dIndex] = top.factor_id.split('_').map(n => parseInt(n, 10));
    const factorFeedbackText = (() => {
      const feedbacks = [];
      orgAll.forEach(entry => {
        const fb = entry?.feedback || [];
        fb.forEach(q => {
          if (q?.factor === fIndex && q?.id === dIndex) {
            if (q?.prompt_question) {
              if (q?.feedback) feedbacks.push(q.feedback);
              if (q?.follow_up) feedbacks.push(q.follow_up);
            } else if (q?.feedback) {
              feedbacks.push(q.feedback);
            }
          }
        });
      });
      return feedbacks.join("\n");
    })();
    if (factorFeedbackText && factorFeedbackText.trim().length > 0) {
      const prompt = `Outcome: ${outcome.outcome_name}\nDriver: ${top.factor_name}\n\nFeedback (from driver):\n${factorFeedbackText}\n\nTask: Based on this feedback, write EXACTLY 3 bullet points. For EACH bullet, write 2-3 concise sentences that synthesize the core themes in the Driver that are influencing the Outcome. Focus on what themes from the feedback about the Driver are driving the Outcome (be specific, no fluff). Return ONLY a JSON array of 3 strings, where each string is one bullet paragraph.`;
      const res = await callLLM(prompt, updateTokens, selectedModel, customInstructions);
      const parsed = parseGPTJsonResponse(res.content);
      const sentences = Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.sentences) ? parsed.sentences : []);
      outcome.driver_analysis.sentences = sentences;
    }
  }

  // By anchor breakdown
  const byAnchor = [];
  for (const option of selectedAnchorParent.options) {
    const anchorID = option.id;
    const anchorName = option.name;
    const filteredData = filter_data(orgAll, [{ id: anchorID }]);
    const response_count = Array.isArray(filteredData) ? filteredData.length : 0;

    // Outcomes per anchor (section average)
    const perSection = computeOutcomeSectionStats(filteredData, outcomeQuestions);
    const outcomesList = (outcomeQuestions?.questions || []).map(section => ({
      outcome_id: section.id,
      overall_score: (perSection[section.id]?.average) || 0,
      standard_average: ((outcomeStandards?.[Number(section.id)] || outcomeStandards?.[String(section.id)])?.overall_average ?? null)
    }));

    // Factor averages per anchor
    const factorAverages = calculateAverageScoresPerFactor(filteredData);
    const factorsList = Object.keys(factorAverages).map(key => {
      const [f, d] = key.split('_').map(n => parseInt(n, 10));
      return { factor_id: key, factor_name: getFactorName(questions, [f, d]), average_score: factorAverages[key] };
    });

    byAnchor.push({
      anchor_id: anchorID,
      anchor_name: anchorName,
      response_count,
      outcomes: outcomesList,
      factors: factorsList
    });
  }

  // Removed Top/Bottom 5 factor question scores per requirements

  // Strengths/Weaknesses by question distribution
  const allFactorQuestions = formatFactorQuestionList(orgAll, questions);
  const { strengths, weaknesses } = selectTopStrengthsWeaknessesByQuestion(allFactorQuestions);

  // LLM themes for strengths and weaknesses (question-level, use only that question's feedback)
  async function llmThemesForQuestion(qItem, scoreCategory) {
    const [dimIndex, factorIndex, localQIndex] = qItem.question_id.split('_').map(n => parseInt(n, 10));
    const commentStrings = collectQuestionCommentStrings(orgAll, dimIndex, factorIndex, localQIndex);
    const comment_count = Array.isArray(commentStrings) ? commentStrings.length : 0;
    const questionFeedbackText = commentStrings.join("\n");

    if (!questionFeedbackText) {
      return {
        comment_count,
        thematic_analysis: {
          overview: "There are not enough comments to generate a thematic analysis for this area.",
          comments: []
        }
      };
    }
    const numericScore = typeof qItem.average === "number" ? qItem.average.toFixed(2) : String(qItem.average ?? "");
    const prompt = `
You are an engagement survey interpretation specialist generating qualitative summaries that support quantitative engagement survey results. Your role is to help leaders understand the meaning of scores and the themes present in employee comments. Maintain a neutral, analytic, descriptive tone with no assumptions, no bias, no recommendations, and no attribution of motives.

Your task is to generate a detailed thematic analysis for a single driver item (survey question) that has been identified as either a Top Strength or an Area to Watch. Your output must reflect the themes present in the comments while aligning with the score category.

INPUTS:
- driver_item: ${qItem.question_text}
- score_category: "${scoreCategory}"
- numeric_score: ${numericScore}
- list_of_comments: each line in the COMMENTS section below is one employee comment.

COMMENTS:
${questionFeedbackText}

DATA RULES:
1. A "usable comment" contains meaningful content related to the driver item AND is aligned with the score_category:
   - For score_category = "Strength": only use comments that describe positive experiences, appreciation, or favourable conditions related to the driver item.
   - For score_category = "Area to Watch": only use comments that describe challenges, gaps, concerns, or negative experiences related to the driver item.
   Exclude:
   - 1–2 word responses
   - irrelevant or off-topic remarks
   - comments not related to the driver item
   - comments whose sentiment does not match the required valence for the score_category (treat these as not usable for this output).

2. If there are fewer than 3 usable comments, apply the LOW-DATA RULE and do NOT infer themes.
   - In this case, set thematic_analysis.overview to exactly: "There are not enough comments to generate a thematic analysis for this area."
   - Set thematic_analysis.comments to an empty list [].

3. When comments differ or conflict (within the same valence), acknowledge this in the overview without implying magnitude or frequency.

4. Never imply numerical patterns (no “most,” “many,” “majority,” or “several”).

5. Never fabricate themes, behavioural patterns, or interpretations not supported by the comments.

THEMATIC ANALYSIS RULES:
- For score_category = "Strength":
  - Focus only on positive aspects of the employee experience based on the comments.
  - Describe what employees value, appreciate, or experience positively.
  - Do not include or infer negative themes, and do not offer advice or improvement suggestions.

- For score_category = "Area to Watch":
  - Focus only on opportunities for improvement based on the comments.
  - Describe challenges, barriers, gaps, or points of friction noted in the comments.
  - Do not include or infer positive themes, and do not assign blame or speculate about causes.

OVERVIEW PARAGRAPH:
- Provide a single paragraph of 3–5 sentences.
- Describe the key themes present in the usable comments, aligned with the score_category.
- Maintain a neutral, descriptive tone with no recommendations, no predictions, and no attribution of motives.

COMMENTS SELECTION:
- Select 3–4 verbatim comments that best illustrate and support the themes described in the overview.
- Only select from usable comments (correct valence, relevant to the driver item, and containing meaningful content).
- Do not edit or summarise the comments, other than removing personally identifying information if present.

OUTPUT JSON STRUCTURE:
Return ONLY a JSON object with this exact structure and no additional fields:
{
  "thematic_analysis": {
    "overview": "One concise paragraph summarizing the key themes for this driver item, aligned with the score_category and based only on usable comments.",
    "comments": [
      "Representative comment 1",
      "Representative comment 2",
      "Representative comment 3",
      "Optional representative comment 4"
    ]
  }
}

If the LOW-DATA RULE applies (fewer than 3 usable comments after filtering), return:
{
  "thematic_analysis": {
    "overview": "There are not enough comments to generate a thematic analysis for this area.",
    "comments": []
  }
}

Return only valid JSON, with no markdown formatting, no backticks, and no explanatory text.`;
    const res = await callLLM(prompt, updateTokens, selectedModel, customInstructions);
    const parsed = parseGPTJsonResponse(res.content);
    return {
      comment_count,
      thematic_analysis: {
        overview:
          typeof parsed?.thematic_analysis?.overview === "string"
            ? parsed.thematic_analysis.overview
            : typeof parsed?.overview === "string"
              ? parsed.overview
              : "",
        comments: Array.isArray(parsed?.thematic_analysis?.comments)
          ? parsed.thematic_analysis.comments
          : Array.isArray(parsed?.comments)
            ? parsed.comments
            : []
      }
    };
  }

  const strengthsWithThemes = [];
  for (const s of strengths) {
    const themes = await llmThemesForQuestion(s, "Strength");
    const qText = s?.question_text ?? (() => {
      try {
        const [dimIndex, factorIndex, localQIndex] = String(s?.question_id || "")
          .split("_")
          .map((n) => parseInt(n, 10));
        const qObj = questions?.dimensions?.[dimIndex]?.factors?.[factorIndex]?.questions?.[localQIndex];
        return (qObj?.question || qObj?.q || null);
      } catch (e) { return null; }
    })();
    strengthsWithThemes.push({
      ...s,
      question_text: qText || "",
      comment_count: themes?.comment_count ?? 0,
      llm: { thematic_analysis: themes?.thematic_analysis }
    });
  }
  const weaknessesWithThemes = [];
  for (const w of weaknesses) {
    const themes = await llmThemesForQuestion(w, "Area to Watch");
    const qText = w?.question_text ?? (() => {
      try {
        const [dimIndex, factorIndex, localQIndex] = String(w?.question_id || "")
          .split("_")
          .map((n) => parseInt(n, 10));
        const qObj = questions?.dimensions?.[dimIndex]?.factors?.[factorIndex]?.questions?.[localQIndex];
        return (qObj?.question || qObj?.q || null);
      } catch (e) { return null; }
    })();
    weaknessesWithThemes.push({
      ...w,
      question_text: qText || "",
      comment_count: themes?.comment_count ?? 0,
      llm: { thematic_analysis: themes?.thematic_analysis }
    });
  }

  // Overall per-question and per-factor scores
  const overall_question_scores = formatFactorQuestionList(orgAll, questions).map(i => ({
    factor_id: i.factor_id,
    factor_name: i.factor_name,
    question_id: i.question_id,
    question_text: i.question_text,
    average: i.average,
    distribution: i.distribution
  }));
  const factor_scores = factorDistributionAcrossOrg(orgAll, questions).map(i => ({
    factor_id: i.factor_id,
    factor_name: i.factor_name,
    distribution: i.distribution,
    average: average(gatherFactorValues(orgAll, parseInt(i.factor_id.split('_')[1], 10), parseInt(i.factor_id.split('_')[0], 10), questions))
  }));

  // Token costs
  const inputCost = (totalTokenUsage.prompt_tokens / 1000000) * 2;
  const outputCost = (totalTokenUsage.completion_tokens / 1000000) * 8;
  const totalCost = inputCost + outputCost;

  return {
    selected_anchor_parent: {
      id: selectedAnchorParent.id,
      name: selectedAnchorParent.name
    },
    participation: {
      overall: overallParticipation,
      // Temporarily disable by-anchor participation breakdown; the underlying
      // employee_categories structure is paginated and does not yet support
      // accurate anchor-level participation.
      by_anchor: []
    },
    outcomes: outcomesPayload,
    by_anchor: byAnchor,
    strengths_weaknesses: { strengths: strengthsWithThemes, weaknesses: weaknessesWithThemes },
    overall_question_scores,
    factor_scores,
    token_usage: { ...totalTokenUsage, input_cost: inputCost, output_cost: outputCost, total_cost: totalCost }
  };
}

// ----- Organization report with demographics -----
export async function calculateOrganizationReportWithDemographics(
  raw_data,
  core_data,
  debriefSchedules,
  anchorParentID,
  onTokenUpdate = null,
  selectedModel = "gpt-4.1",
  selectedAnchors,
  combineAnchors,
  surveyStructure,
  customInstructions = ""
) {
  // First run the base organization report logic
  const baseResult = await calculateOrganizationReport(
    raw_data,
    core_data,
    debriefSchedules,
    anchorParentID,
    onTokenUpdate,
    selectedModel,
    selectedAnchors,
    combineAnchors,
    customInstructions
  );

  const orgAll = raw_data?.[0]?.responses || [];
  const outcomeQuestions = core_data?.outcomeQuestions;

  // Derive outcome standards like in the base report
  const outcomeStandards = (() => {
    const list = core_data?.get_standards || [];
    const sortOrder = outcomeQuestions?.sort_order;
    const matched = list.find(
      (f) => Array.isArray(f?.question_sort_order) && f.question_sort_order.includes(sortOrder)
    );
    return matched?.response || null;
  })();

  // All demographic categories (no priority restriction), prefer full survey structure over filtered core_data
  const rootStruct = Array.isArray(surveyStructure) && surveyStructure[0]
    ? surveyStructure[0]
    : (Array.isArray(core_data?.categories) ? core_data.categories[0] : null);

  const demographicCategories =
    rootStruct?.categories?.filter((cat) => cat.demographic) || [];

  const byDemographic = demographicCategories.map((demographic) => {
    const demographicOptions = demographic.options || [];

    // Option-level breakdown (existing behaviour)
    const options = demographicOptions.map((option) => {
      const filteredData = filter_data(orgAll, [{ id: option.id }]) || [];
      const responseCount = filteredData.length;

      // Outcomes per demographic option (section average)
      const perSection = computeOutcomeSectionStats(filteredData, outcomeQuestions);
      const outcomesList = (outcomeQuestions?.questions || []).map((section) => ({
        outcome_id: section.id,
        overall_score: (perSection[section.id]?.average) || 0,
        standard_average:
          (
            outcomeStandards?.[Number(section.id)] ||
            outcomeStandards?.[String(section.id)]
          )?.overall_average ?? null
      }));

      return {
        option_id: option.id,
        option_name: option.name,
        response_count: responseCount,
        outcomes: outcomesList
      };
    });

    // Group-level breakdown (e.g., Ethnicity groups like "Asian", "Black", etc.)
    const optionsWithGroup = demographicOptions.filter((opt) => opt.group);
    const group_averages = [];

    if (optionsWithGroup.length > 0) {
      const groupMap = {};

      // Build a map of group key -> { label, optionIds[] }
      optionsWithGroup.forEach((opt) => {
        const groupKey = opt.group;
        if (!groupMap[groupKey]) {
          const titleObj = demographic.group_titles?.[groupKey] || {};
          const displayName =
            titleObj.EN ||
            titleObj.FR ||
            titleObj.ES ||
            titleObj.POR ||
            groupKey;

          groupMap[groupKey] = {
            group_key: groupKey,
            group_name: displayName,
            optionIds: []
          };
        }
        groupMap[groupKey].optionIds.push(opt.id);
      });

      Object.values(groupMap).forEach((groupInfo) => {
        const filters = groupInfo.optionIds.map((id) => ({ id }));
        const filteredData = filter_data(orgAll, filters) || [];
        const responseCount = filteredData.length;

        const perSection = computeOutcomeSectionStats(filteredData, outcomeQuestions);
        const outcomesList = (outcomeQuestions?.questions || []).map((section) => ({
          outcome_id: section.id,
          overall_score: (perSection[section.id]?.average) || 0,
          standard_average:
            (
              outcomeStandards?.[Number(section.id)] ||
              outcomeStandards?.[String(section.id)]
            )?.overall_average ?? null
        }));

        // Overall average across all outcome sections for this group
        const outcomeAverages = Object.values(perSection || {})
          .map((s) => (s && typeof s.average === "number" ? s.average : null))
          .filter((v) => typeof v === "number" && !Number.isNaN(v));
        const overallScore = average(outcomeAverages);

        group_averages.push({
          group_key: groupInfo.group_key,
          group_name: groupInfo.group_name,
          response_count: responseCount,
          overall_score: overallScore,
          outcomes: outcomesList
        });
      });
    }

    return {
      demographic_id: demographic.id,
      demographic_name: demographic.name,
      options,
      group_averages
    };
  });

  return {
    ...baseResult,
    by_demographic: byDemographic
  };
}
